import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { Alert, Button, Chip, Empty, Panel, Spinner, Table } from '../ui';
import { invalidateAll } from '../api/write-keys';

/** One row of `GET /auth/devices` (or the admin equivalent). Never carries the token. */
export interface TrustedDevice {
  id: string;
  label: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  state: 'ACTIVE' | 'EXPIRED' | 'REVOKED';
  /** True for the browser making the request — retailer view only. */
  current: boolean;
}

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

/**
 * Round-2 finding C4 — the devices a retailer user has trusted (ON-06a), and a way
 * to take one back. A lost laptop stays trusted for up to 30 days otherwise.
 *
 * A self-contained panel so that neither R-18 Account (the retailer's own list) nor
 * A-04 Retailer (an administrator acting on a user's behalf) has to change shape
 * to carry it: each places `<TrustedDevices />` where it fits.
 *
 *   <TrustedDevices />                         R-18 — the signed-in retailer user's own
 *   <TrustedDevices retailerUserId={u.id} />   A-04 — needs retailer.view; revoke needs retailer.hold
 *
 * Revoke is a two-step confirm in place, not `window.confirm`: a browser dialog
 * blocks the page and cannot be styled or translated.
 */
export function TrustedDevices({ retailerUserId }: { retailerUserId?: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const base = retailerUserId ? `/auth/retailer-users/${retailerUserId}/devices` : '/auth/devices';
  const key = ['trusted-devices', retailerUserId ?? 'me'];
  const { data, isLoading, error } = useQuery({ queryKey: key, queryFn: () => api.get<TrustedDevice[]>(base) });
  const [arming, setArming] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function revoke(id: string) {
    setFailure(null);
    setBusy(true);
    try {
      await api.post(`${base}/${id}/revoke`);
      setArming(null);
      await invalidateAll(qc, [key]);
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : t('devices.revokeFailed'));
    } finally {
      setBusy(false);
    }
  }

  const tone = { ACTIVE: 'done', EXPIRED: 'lapse', REVOKED: 'stop' } as const;

  return (
    <Panel title={t('devices.title')} pad>
      <p className="mk-dim" style={{ marginTop: 0 }}>{t('devices.body')}</p>
      {failure ? <Alert tone="stop">{failure}</Alert> : null}
      {isLoading ? <Spinner /> : null}
      {error ? <Alert tone="stop">{(error).message}</Alert> : null}
      {data ? (
        <Table
          rows={data}
          rowKey={(d) => d.id}
          empty={<Empty title={t('devices.emptyTitle')} body={t('devices.emptyBody')} />}
          columns={[
            {
              header: t('devices.colDevice'),
              render: (d) => (
                <>
                  {d.label ?? t('devices.unknown')}
                  {d.current ? <> <Chip tone="us">{t('devices.thisOne')}</Chip></> : null}
                </>
              ),
            },
            { header: t('devices.colState'), center: true, render: (d) => <Chip tone={tone[d.state]}>{t(`devices.state.${d.state}`)}</Chip> },
            { header: t('devices.colLastSeen'), render: (d) => <span className="mk-dim">{when(d.lastSeenAt)}</span> },
            {
              header: t('devices.colUntil'),
              render: (d) => <span className="mk-dim">{d.state === 'REVOKED' ? when(d.revokedAt) : when(d.expiresAt)}</span>,
            },
            {
              header: '',
              render: (d) => d.state === 'REVOKED' ? null : arming === d.id ? (
                <>
                  <Button size="sm" tone="danger" disabled={busy} onClick={() => void revoke(d.id)}>{t('devices.confirmRevoke')}</Button>{' '}
                  <Button size="sm" tone="ghost" disabled={busy} onClick={() => setArming(null)}>{t('common.cancel')}</Button>
                </>
              ) : (
                <Button size="sm" tone="ghost" onClick={() => setArming(d.id)}>{t('devices.revoke')}</Button>
              ),
            },
          ]}
        />
      ) : null}
    </Panel>
  );
}
